// Admin: invitation e-mail for new blåtur members, built from the rules, earlier trips and their group photos.
// Copy it (with images) into your own e-mail program; the recipients are added in BCC.

import React, { useEffect, useMemo, useState } from 'react';
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore';
import { FiCopy, FiMail } from 'react-icons/fi';
import { app } from './firebase-config';
import { BLATUR_TYPE, tripYear, tripTitle, tripCover } from './blaturData';
import { normalizeName, createPerson } from './adminData';
import { NamePicker } from './AdminEvents';
import { loadMembers, loadEmailsByName } from './blaturMembers';
import { invitationEmail, mailtoLink, DEFAULT_INVITATION_INTRO } from './blaturEmails';

const db = getFirestore(app);
const FEATURED_YEARS = [2017, 2018, 2026]; // Pula, München and the Toscana roadtrip from Pisa

function AdminInviteEmail({ user, roles, directory, reloadDirectory }) {
  const year = roles.config?.year || new Date().getFullYear();
  const [trips, setTrips] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [intro, setIntro] = useState(DEFAULT_INVITATION_INTRO);
  const [recipients, setRecipients] = useState(null);
  const [emails, setEmails] = useState({});
  const [status, setStatus] = useState('');

  useEffect(() => {
    const load = async () => {
      const [t, members, e] = await Promise.all([
        getDocs(query(collection(db, 'events'), where('event_type', '==', BLATUR_TYPE))),
        loadMembers().catch(() => []),
        loadEmailsByName(directory).catch(() => ({})),
      ]);
      const list = t.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => tripYear(a) - tripYear(b));
      setTrips(list);
      setEmails(e);
      setFeatured(list.filter(trip => FEATURED_YEARS.includes(tripYear(trip))).map(trip => trip.id));
      // Default recipients: people on the invitation list who have never been on a blåtur
      const been = new Set(list.flatMap(trip => [...(trip.Deltagelse || []), ...(trip.Vert || [])]).map(normalizeName));
      setRecipients(members.map(m => m.name.trim()).filter(n => !been.has(normalizeName(n))));
    };
    load();
  }, [directory]);

  const organizerNames = (roles.config?.organizers || [])
    .map(uid => directory.users.find(u => u.id === uid)?.name?.trim()).filter(Boolean);

  const email = useMemo(() => invitationEmail({
    year,
    organizers: organizerNames,
    intro,
    featuredTrips: trips.filter(t => (featured || []).includes(t.id)),
    allTrips: trips,
    origin: window.location.origin,
  }), [year, organizerNames.join(','), intro, trips, featured]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!featured || !recipients) return <p className="admin-hint">Laster…</p>;

  const recipientEmails = recipients.map(n => emails[normalizeName(n)]).filter(Boolean);
  const missing = recipients.filter(n => !emails[normalizeName(n)]);
  const missingPhotos = trips.filter(t => featured.includes(t.id) && !tripCover(t));

  const copy = async () => {
    try {
      await navigator.clipboard.write([new window.ClipboardItem({
        'text/html': new Blob([email.html], { type: 'text/html' }),
        'text/plain': new Blob([email.text], { type: 'text/plain' }),
      })]);
      setStatus('Kopiert! Lim inn i e-posten (Cmd+V).');
    } catch (error) {
      await navigator.clipboard.writeText(email.text);
      setStatus('Kopierte teksten uten bilder (nettleseren støtter ikke kopiering med bilder).');
    }
  };

  const open = () => {
    window.location.href = mailtoLink({ bcc: recipientEmails, subject: email.subject });
  };

  return (
    <div>
      <section className="admin-form">
        <h2>Invitasjonsmail til nye</h2>
        <p className="admin-hint">
          Fyll inn mottakere og velg turer. Kopier deretter e-posten og åpne e-postprogrammet. Mottakerne legges i BCC og emnet
          fylles inn, og så limer du inn teksten med bildene.
        </p>

        <NamePicker
          label="Mottakere"
          value={recipients}
          onChange={setRecipients}
          names={directory.names}
          onCreatePerson={async (name) => { await createPerson({ name }, user.uid); await reloadDirectory(); }}
        />
        {missing.length > 0 && (
          <p className="admin-hint">
            Mangler e-post: {missing.join(', ')}. Legg dem inn i e-posten selv, eller opprett personen med e-post under Personer.
          </p>
        )}

        <div className="admin-field">
          <label>Turer som vises frem</label>
          <div className="admin-checklist">
            {trips.map(t => (
              <label key={t.id} className={featured.includes(t.id) ? 'checked' : ''}>
                <input
                  type="checkbox"
                  checked={featured.includes(t.id)}
                  onChange={() => setFeatured(f => (f.includes(t.id) ? f.filter(x => x !== t.id) : [...f, t.id]))}
                />
                {tripYear(t)} {tripTitle(t)}
              </label>
            ))}
          </div>
          {missingPhotos.length > 0 && (
            <p className="admin-hint">
              Uten gruppebilde: {missingPhotos.map(t => `${tripYear(t)} ${tripTitle(t)}`).join(', ')}. Last opp under Bilder,
              og velg gruppebilde når du redigerer turen.
            </p>
          )}
        </div>

        <div className="admin-field">
          <label>Innledning</label>
          <textarea rows={7} value={intro} onChange={(e) => setIntro(e.target.value)} />
        </div>

        <div className="admin-actions">
          <button className="button-17" onClick={copy}><FiCopy /> Kopier e-post med bilder</button>
          <button className="admin-btn" onClick={open}><FiMail /> Åpne e-post til {recipientEmails.length} mottakere</button>
          {status && <span className="admin-status">{status}</span>}
        </div>
      </section>

      <section className="admin-form">
        <h2>Forhåndsvisning</h2>
        <p className="admin-hint">Emne: {email.subject}</p>
        <div className="admin-email-preview" dangerouslySetInnerHTML={{ __html: email.html }} />
      </section>
    </div>
  );
}

export default AdminInviteEmail;
